/**
 * Sprint 10 Batch 2.1: Usage Stats Card (Enhanced with Real-Time Tracking)
 * Shows current usage against plan limits with live updates
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  Users, FileText, Mail, TrendingUp, 
  AlertTriangle, CheckCircle, ArrowRight, RefreshCw
} from 'lucide-react';

const UsageStatsCard = ({ 
  usage = {},
  limits = {},
  packageName = 'Trial',
  onUpgrade,
  // Real-time props
  realTimeUsage = null,
  onRefresh = null,
  isRefreshing = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Use real-time usage if provided, otherwise fall back to static usage
  const currentUsage = realTimeUsage || usage;
  
  // FIX P0-A: Read both camelCase and snake_case keys — NO hardcoded fallback defaults
  const leadsLimit = limits.leads ?? limits.leads_per_month ?? limits.leadsPerMonth;
  const salesRepsLimit = limits.salesReps ?? limits.sales_reps;
  const offersLimit = limits.offersPerMonth ?? limits.offers_per_month;

  // Calculate usage for each metric
  const metrics = [
    {
      key: 'leads',
      label: t('usage.leads', 'Leads'),
      icon: FileText,
      current: currentUsage.leads || 0,
      max: leadsLimit != null ? leadsLimit : 0,
    },
    {
      key: 'salesReps',
      label: t('usage.salesReps', 'Included users'),
      icon: Users,
      current: currentUsage.salesReps || 0,
      max: salesRepsLimit != null ? salesRepsLimit : 0,
    },
    {
      key: 'offers',
      label: t('usage.offersMonth', 'Offers (Monthly)'),
      icon: Mail,
      current: currentUsage.offersThisMonth || 0,
      max: offersLimit != null ? offersLimit : 0,
    },
  ];
  
  const getPercentage = (current, max) => {
    if (max === -1) return 0; // Unlimited
    return Math.min(100, Math.round((current / max) * 100));
  };
  
  const getStatusColor = (percentage, isUnlimited) => {
    if (isUnlimited) return 'text-green-500';
    if (percentage >= 100) return 'text-red-500';
    if (percentage >= 80) return 'text-amber-500';
    return 'text-green-500';
  };
  
  const getProgressColor = (percentage, isUnlimited) => {
    if (isUnlimited) return 'bg-green-500';
    if (percentage >= 100) return 'bg-red-500';
    if (percentage >= 80) return 'bg-amber-500';
    return 'bg-green-500';
  };
  
  const hasAnyLimitReached = metrics.some(m => m.max !== -1 && m.current >= m.max);
  const hasAnyNearLimit = metrics.some(m => m.max !== -1 && m.current >= m.max * 0.8 && m.current < m.max);
  
  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      navigate('/clinic/packages');
    }
  };
  
  return (
    <Card className="shadow-sm" data-testid="usage-stats-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-500" />
            {t('usage.title', 'Plan Usage')}
          </CardTitle>
          <div className="flex items-center gap-2">
            {onRefresh && (
              <button 
                onClick={onRefresh}
                disabled={isRefreshing}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title={t('usage.refresh', 'Refresh usage')}
              >
                <RefreshCw className={`h-4 w-4 text-gray-400 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
            )}
            <span className="text-sm font-medium text-gray-500">
              {packageName}
            </span>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {metrics.map((metric) => {
          const isUnlimited = metric.max === -1;
          const percentage = getPercentage(metric.current, metric.max);
          const Icon = metric.icon;
          const statusColor = getStatusColor(percentage, isUnlimited);
          const progressColor = getProgressColor(percentage, isUnlimited);
          
          return (
            <div key={metric.key} className="space-y-1.5" data-testid={`usage-metric-${metric.key}`}>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${statusColor}`} />
                  <span className="text-gray-700">{metric.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  {isUnlimited ? (
                    <span className="text-green-600 font-medium flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      {t('usage.unlimited', 'Unlimited')}
                    </span>
                  ) : (
                    <>
                      <span className={`font-medium ${statusColor}`}>
                        {metric.current}
                      </span>
                      <span className="text-gray-400">/</span>
                      <span className="text-gray-600">{metric.max}</span>
                      {percentage >= 100 && (
                        <AlertTriangle className="h-3 w-3 text-red-500" />
                      )}
                    </>
                  )}
                </div>
              </div>
              
              {!isUnlimited && (
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-500 rounded-full ${progressColor}`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              )}
            </div>
          );
        })}
        
        {/* Upgrade prompt if near/at limits */}
        {(hasAnyLimitReached || hasAnyNearLimit) && (
          <div className={`mt-4 p-3 rounded-lg ${
            hasAnyLimitReached ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'
          }`}>
            <div className="flex items-center justify-between gap-4">
              <p className={`text-sm ${hasAnyLimitReached ? 'text-red-700' : 'text-amber-700'}`}>
                {hasAnyLimitReached 
                  ? t('usage.limitReached', 'You\'ve reached your plan limit')
                  : t('usage.nearLimit', 'You\'re approaching your plan limit')
                }
              </p>
              <Button 
                size="sm"
                onClick={handleUpgrade}
                className={hasAnyLimitReached ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
                data-testid="usage-upgrade-btn"
              >
                {t('usage.upgrade', 'Upgrade')}
                <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UsageStatsCard;
